import { Collapse, Flex, Grid, Image } from '@chakra-ui/react'
import { callAll, cx } from '@chakra-ui/shared-utils'
import type {
  HTMLChakraProps,
  PropsOf,
  SystemStyleObject,
  ThemingProps
} from '@chakra-ui/system'
import {
  chakra,
  forwardRef,
  keyframes,
  omitThemingProps,
  useMultiStyleConfig,
} from '@chakra-ui/system'
import { DiscountType, type Discount } from 'BrokerTsLib/src/dtos'
import { cloneElement, useMemo } from 'react'

import { Insurer } from 'BrokerTsLib/src/enums'
import { useCompanyCheckboxGroupContext } from './checkbox-context'
import { CheckboxIcon } from './checkbox-icon'
import type {
  CompanyCheckboxOptions,
  UseCompanyCheckboxProps,
} from './checkbox-types'
import CheckboxDiscount from './checkboxDiscount'
import CheckboxVoucher from './checkboxVoucher'
import { useCheckbox } from './use-checkbox'

const controlStyles: SystemStyleObject = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  verticalAlign: 'top',
  userSelect: 'none',
  flexShrink: 0,
}

const rootStyles: SystemStyleObject = {
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'top',
  position: 'relative',
}

const checkAnim = keyframes({
  from: {
    opacity: 0,
    strokeDashoffset: 16,
    transform: 'scale(0.95)',
  },
  to: {
    opacity: 1,
    strokeDashoffset: 0,
    transform: 'scale(1)',
  },
})

const indeterminateOpacityAnim = keyframes({
  from: {
    opacity: 0,
  },
  to: {
    opacity: 1,
  },
})
const indeterminateScaleAnim = keyframes({
  from: {
    transform: 'scaleX(0.65)',
  },
  to: {
    transform: 'scaleX(1)',
  },
})

type CheckboxControlProps = Omit<
  HTMLChakraProps<'div'>,
  keyof UseCompanyCheckboxProps
>

type BaseInputProps = Pick<
  PropsOf<'input'>,
  'onBlur' | 'checked' | 'defaultChecked'
>

export interface CompanyCheckboxProps
  extends CheckboxControlProps,
  BaseInputProps,
  ThemingProps<'Checkbox'>,
  UseCompanyCheckboxProps,
  CompanyCheckboxOptions {
  logo?: string,
  showDiscounts?: boolean,
  discounts?: Discount[],
  insurerId?: number,
  onDiscountChange?: (insuranceCategoryDiscountId: number, value: number | null) => void
  onVoucherChange?: (insuranceCategoryDiscountId: number, value: string ) => void
}

/**
 * Checkbox
 *
 * React component used in forms when a user needs to select
 * multiple values from several options.
 *
 * @see Docs https://chakra-ui.com/checkbox
 * @see WAI-ARIA https://www.w3.org/WAI/ARIA/apg/patterns/checkbox/
 */
export const CompanyCheckbox = forwardRef<CompanyCheckboxProps, 'input'>(
  function Checkbox(props, ref) {
    const group = useCompanyCheckboxGroupContext()

    const mergedProps = { ...group, ...props } as CompanyCheckboxProps
    const styles = useMultiStyleConfig('Checkbox', mergedProps)

    const ownProps = omitThemingProps(props)

    const {
      spacing = '0.5rem',
      className,
      children,
      iconColor,
      iconSize,
      icon = <CheckboxIcon />,
      isChecked: isCheckedProp,
      isDisabled = group?.isDisabled,
      onChange: onChangeProp,
      inputProps,
      logo,
      showDiscounts,
      discounts,
      onDiscountChange,
      onVoucherChange,
      insurerId,
      ...rest
    } = ownProps

    let isChecked = isCheckedProp
    if (!isCheckedProp // pokud posilam pres props, tak nemenim na pozadi
      && (group?.value && ownProps.value)) {
      isChecked = group.value.includes(ownProps.value)
    }

    let onChange = onChangeProp
    if (group?.onChange && ownProps.value) {
      onChange = callAll(group.onChange, onChangeProp)
    }

    const {
      state,
      getInputProps,
      getCheckboxProps,
      getLabelProps,
      getRootProps,
    } = useCheckbox({
      ...rest,
      isDisabled,
      isChecked,
      onChange,
    })

    const iconStyles: SystemStyleObject = useMemo(
      () => ({
        animation: state.isIndeterminate
          ? `${indeterminateOpacityAnim} 20ms linear, ${indeterminateScaleAnim} 200ms linear`
          : `${checkAnim} 200ms linear`,
        fontSize: iconSize,
        color: iconColor,
        ...styles.icon,
      }),
      [iconColor, iconSize, state.isIndeterminate, styles.icon],
    )

    const clonedIcon = cloneElement(icon, {
      __css: iconStyles,
      isIndeterminate: state.isIndeterminate,
      isChecked: state.isChecked,
    })

    const isGcpVoucher = (insId : number, discountType : DiscountType) => {
      if (insId == Insurer.GeneraliCeskaPojistovna && 
          (discountType == DiscountType.Voucher || discountType == DiscountType.VoucherPov ||
            discountType == DiscountType.VoucherHav)) {
        return true
      }
      return false
    }

    return (
      <chakra.label
        __css={{ ...rootStyles, ...styles.container }}
        className={cx('chakra-checkbox', className)}
        sx={{
          display: 'grid',
          alignItems: 'stretch',
          gridTemplateColumns: logo ? 'auto 1fr' : '1fr',
          gap: 1,
        }}
        {...getRootProps()}
      >
        <input
          className="chakra-checkbox__input"
          {...getInputProps(inputProps, ref)}
        />

        {logo && (
          <Flex
            sx={{
              px: 4,
              py: 2,
              bgColor: 'neutral.50',
              borderLeftRadius: 'sm',
              alignItems: 'center',
            }}
          >
            <Image src={logo} alt="" sx={{ w: 16, maxH: 12 }} />
          </Flex>
        )}

        <Flex
          gap={2}
          sx={{
            alignItems: 'start',
            justifyContent: 'center',
            bgColor: 'neutral.50',
            borderRightRadius: 'sm',
            flexDir: 'column'
          }}
        >
          <Flex
            __css={{ ...styles.container }}
            sx={{
              px: 4,
              py: 4,
            }}
          >
            <chakra.span
              __css={{ ...controlStyles, ...styles.control }}
              className="chakra-checkbox__control"
              {...getCheckboxProps()}
            >
              {clonedIcon}
            </chakra.span>

            {children && (
              <chakra.span
                className="chakra-checkbox__label"
                {...getLabelProps()}
                __css={{
                  marginStart: spacing,
                  ...styles.label,
                }}
              >
                {children}
              </chakra.span>
            )}
          </Flex>

          {discounts && discounts.length > 0 &&
            <Collapse in={showDiscounts}>
              <Grid
                w='100%'
                templateColumns='1fr'
                gap={4}
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                sx={{
                  px: 4,
                  pb: 4,
                  cursor: ''
                }}
              >
                {discounts.filter(d => !isGcpVoucher(insurerId as number, d.type!)).map(x => (
                  <CheckboxDiscount
                    key={x.insuranceCategoryDiscountId}
                    discount={x}
                    onChange={(value) => {
                      onDiscountChange?.(x.insuranceCategoryDiscountId, value ?? null)
                    }}
                  />
                ))}
                {discounts.filter(d => isGcpVoucher(insurerId as number, d.type!)).map(x => (
                  <CheckboxVoucher
                    key={x.insuranceCategoryDiscountId}
                    discount={x}
                    onChange={(value) => {
                      onVoucherChange?.(x.insuranceCategoryDiscountId, value)
                    }}
                  />
                ))}
              </Grid>
            </Collapse>
          }

        </Flex>

      </chakra.label >
    )
  },
)

CompanyCheckbox.displayName = 'CompanyCheckboxGroup'
